import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { authService } from './authService';
const AuthContext = createContext(undefined);
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        initializeAuth();
    }, []);
    const initializeAuth = async () => {
        try {
            setIsLoading(true);
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
            }
        }
        catch (error) {
            console.error('Auth initialization error:', error);
            // Clear any invalid tokens
            await authService.logout();
        }
        finally {
            setIsLoading(false);
        }
    };
    const login = async (email, password) => {
        try {
            const authUser = await authService.signIn(email, password);
            setUser(authUser);
        }
        catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };
    const signup = async (name, email, password) => {
        try {
            await authService.signUp(name, email, password);
            // Note: User will need to verify email before they can sign in
        }
        catch (error) {
            console.error('Signup error:', error);
            throw error;
        }
    };
    const confirmSignUp = async (email, confirmationCode) => {
        try {
            await authService.confirmSignUp(email, confirmationCode);
        }
        catch (error) {
            console.error('Confirm signup error:', error);
            throw error;
        }
    };
    const resendVerificationCode = async (email) => {
        try {
            await authService.resendVerificationCode(email);
        }
        catch (error) {
            console.error('Resend verification error:', error);
            throw error;
        }
    };
    const logout = async () => {
        try {
            await authService.logout();
            setUser(null);
        }
        catch (error) {
            console.error('Logout error:', error);
            // Still clear the user state even if logout fails
            setUser(null);
        }
    };
    const requestPasswordReset = async (email) => {
        try {
            await authService.requestPasswordReset(email);
        }
        catch (error) {
            console.error('Request password reset error:', error);
            throw error;
        }
    };
    const confirmPasswordReset = async (email, confirmationCode, newPassword) => {
        try {
            await authService.confirmPasswordReset(email, confirmationCode, newPassword);
        }
        catch (error) {
            console.error('Confirm password reset error:', error);
            throw error;
        }
    };
    const refreshUser = async () => {
        try {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
            }
        }
        catch (error) {
            console.error('User refresh error:', error);
            throw error;
        }
    };
    const value = {
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        confirmSignUp,
        resendVerificationCode,
        requestPasswordReset,
        confirmPasswordReset,
        logout,
        stopImpersonation: authService.stopImpersonation.bind(authService),
        refreshUser,
    };
    if (isLoading) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gray-50", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto" }), _jsx("p", { className: "mt-4 text-sm text-gray-600", children: "Loading..." })] }) }));
    }
    return (_jsx(AuthContext.Provider, { value: value, children: children }));
}
//# sourceMappingURL=AuthProvider.js.map